/**
 * Vue composable to convert a string into a slug by replacing accents and special characters.
 *
 * @returns {Object} An object containing the `slugify` function.
 */
export function useSlugify() {
  /**
   * Mapping of special characters to their non-accented equivalents.
   *
   * @type {Record<string, string>}
   */
  const charMap: Record<string, string> = {
    ä: 'ae',
    ö: 'oe',
    ü: 'ue',
    ß: 'ss',
    ñ: 'n',
    á: 'a',
    é: 'e',
    í: 'i',
    ó: 'o',
    ú: 'u',
    à: 'a',
    è: 'e',
    ì: 'i',
    ò: 'o',
    ù: 'u',
  };

  /**
   * Converts a given string into a slug by replacing special characters and spaces.
   *
   * @param {string} text - The input text to be slugified.
   * @param {string} [separator="_"] - The separator to use between words (default `_`).
   * @returns {string} The slugified version of the input text.
   */
  const slugify = (text: string, separator: string = '_'): string => {
    if (!text) return '';

    let result = text;
    for (const [char, replacement] of Object.entries(charMap)) {
      result = result.replace(new RegExp(char, 'g'), replacement);
    }

    return result
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, separator)
      .replace(new RegExp(`[^\\w${separator}]`, 'g'), '');
  };

  return { slugify };
}
